"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

const IMAGE_BASE = "/image/new-version/fabrication/laser-cutting/precision-services";

const CARDS = [
	{ image: `${IMAGE_BASE}/1.webp`, translationPrefix: "card1" as const },
	{ image: `${IMAGE_BASE}/2.webp`, translationPrefix: "card2" as const },
	{ image: `${IMAGE_BASE}/3.webp`, translationPrefix: "card3" as const },
];

const DEFAULT_QUOTE_HREF = "/upload";

type Props = {
	quoteHref?: string;
};

export default function LaserCuttingServicesPrecisionParts({ quoteHref = DEFAULT_QUOTE_HREF }: Props) {
	const t = useTranslations("laserCuttingServicesPrecisionParts");

	return (
		<section
			className="bg-white py-8 sm:py-10 md:py-12 lg:py-16"
			aria-labelledby="laser-cutting-services-precision-heading"
		>
			<div className="container px-3 sm:px-4 md:px-6">
				<header className="mb-6 sm:mb-8 md:mb-10 lg:mb-12 text-center">
					<h2
						id="laser-cutting-services-precision-heading"
						className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl"
					>
						{t("sectionTitle")}
					</h2>
					<p className="mt-3 sm:mt-4 text-sm leading-relaxed text-black md:text-base">
						{t("sectionSubtitle")}
					</p>
				</header>

				<div className="grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-5 lg:gap-8">
					{CARDS.map((card) => (
						<article
							key={card.translationPrefix}
							className="flex flex-col overflow-hidden rounded-2xl border-[1.5px] border-[#d0d0d0] bg-white shadow-sm transition-shadow hover:shadow-md"
						>
							<div className="relative aspect-[23/14] w-full overflow-hidden rounded-t-2xl bg-[#f5f5f5]">
								<Image
									src={encodeURI(card.image)}
									alt={t(`${card.translationPrefix}Alt`)}
									fill
									className="object-fill"
									sizes="(max-width: 768px) 100vw, 33vw"
								/>
							</div>
							<div className="flex flex-1 flex-col p-5 sm:p-6">
								<h3 className="mb-3 text-lg font-bold text-black sm:text-xl md:text-2xl">
									{t(`${card.translationPrefix}Title`)}
								</h3>
								<p className="mb-6 flex-1 text-sm leading-relaxed text-black md:text-base">
									{t(`${card.translationPrefix}Desc`)}
								</p>
								<a
									href={quoteHref}
									target="_blank"
									rel="noopener noreferrer"
									className="bg-themePrimary400 inline-flex w-fit items-center justify-center rounded px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-opacity-90 sm:px-6 sm:py-3 sm:text-base"
								>
									{t("getQuote")}
								</a>
							</div>
						</article>
					))}
				</div>
			</div>
		</section>
	);
}
